<template>
  <BaseCard
    :class="$style.card"
    alignment="left"
    @click="(e: MouseEvent) => $handleBlockLinkClick(e, cardLinkEl?.$el)"
  >
    <template v-if="data?.fields?.featuredImage?.fields?.file" #media>
      <AspectRatio ratio="346x190" :class="$style.featuredImage">
        <BasePicture
          :src="
            data?.fields?.featuredImage?.fields?.file?.url +
            '?w=380&h=209&fit=thumb&fm=webp'
          "
          alt=""
        />
      </AspectRatio>
    </template>
    <template v-if="data?.fields?.title" #body>
      <time
        v-if="!data?.fields?.hidePublishedDateTime"
        :datetime="data?.fields?.publishedDateTime"
        :class="$style.time"
      >
        {{ computedLocalPublishedDateTime }}
      </time>
      <h2>
        <NuxtLinkLocale
          ref="cardLink"
          :to="`/news/${data?.fields?.path}`"
          :class="$style.title"
          @click="handleClick"
        >
          <span v-html="$md(data?.fields?.title)" />
        </NuxtLinkLocale>
      </h2>
    </template>
    <template v-if="data?.fields?.tags?.length" #actions>
      <div :class="$style.tags">
        <span
          v-for="(t, i) in data?.fields?.tags"
          :key="t?.sys?.id"
          :class="$style.tag"
        >
          {{
            t?.fields?.label +
            (i < data?.fields?.tags?.length - 1 ? ',&nbsp;' : '')
          }}
        </span>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import type { TypeArticleWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { $handleBlockLinkClick, $md } = useNuxtApp()

const cardLinkEl = useTemplateRef<ComponentPublicInstance | null>('cardLink')

const props = defineProps<{
  data: TypeArticleWithoutUnresolvableLinksResponse | undefined
}>()

const isHydrated = ref(false)

const handleClick = (event: MouseEvent) => {
  // Prevent double click bubble of handleLink
  event.stopPropagation()

  const cardLinkEl = useTemplateRef<HTMLAnchorElement>('cardLink')
  if (cardLinkEl.value) $handleBlockLinkClick(event, cardLinkEl.value)

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'article_click',
    eventParams: {
      event_name: props.data?.fields?.entryTitle,
    },
  })
}

const computedLocalPublishedDateTime = computed(() => {
  const publishedDateTime = props?.data?.fields?.publishedDateTime || ''
  if (!isHydrated.value) return publishedDateTime

  const pub = new Date(publishedDateTime)
  return pub.toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
})

onMounted(() => (isHydrated.value = true))
</script>

<style lang="scss" module>
.card {
  --card-max-width: 576px;

  width: 100%;
  max-width: var(--card-max-width);
  margin: 0 auto;
  cursor: pointer;
}

.featuredImage {
  border: 10px solid var(--white);
  border-bottom: 0;
}

.title {
  color: var(--card-color);
}

.tags {
  color: var(--purple);
}

.time {
  display: block;
  margin-bottom: 0.5em;
  color: #828282;
  font-family: var(--base-font-family);
  font-size: to-rem(16px);
  line-height: math.div(22, 16);
  text-transform: uppercase;
}
</style>
